import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import CameraLoader from "./components/CameraLoader";
import CurrencySelector from "./components/CurrencySelector";
import API_BASE_URL from "./config";
import { formatDuration, formatStorageBytes } from "./formatDuration";
import { formatPrice, detectCurrency } from "./currencyUtils";
import "./Wallet.css";
import submitPaymentForm from "./desktop/submitPaymentForm";

const planFeatures = (plan, tierName = null) => {
  const storageLine = tierName
    ? `${tierName} storage · original quality`
    : plan.max_storage_bytes
      ? `${formatStorageBytes(plan.max_storage_bytes)} storage`
      : null;
  const compressionLine = plan.slug === "pro_max_ultra"
    ? null
    : plan.no_compression
      ? "Original quality — zero compression"
      : plan.max_uncompressed_events
        ? `${plan.max_uncompressed_events} original-quality events/mo`
        : "Smart compression";
  return [
    ...(formatDuration(plan.duration_days) ? [`Valid for ${formatDuration(plan.duration_days)}`] : []),
    plan.max_events === null ? "Unlimited events" : `${plan.max_events} events`,
    plan.max_photos_per_event === null ? "Unlimited photos per event" : `${plan.max_photos_per_event} photos per event`,
    ...(storageLine ? [storageLine] : []),
    ...(compressionLine ? [compressionLine] : []),
    "AI face recognition",
    "Client gallery, QR & live slideshow",
    "Contracts, invoices & PDF proofing",
    "Portfolio page & studio website",
    ...(["pro", "pro_max_ultra"].includes(plan.slug)
      ? ["CRM — leads, branded quotes & follow-ups"] : []),
    ...(plan.slug === "pro_max_ultra" ? ["RAW camera file upload & backup", "Photentra AI · unlimited team members", "Priority support"] : []),
  ];
};

function Wallet() {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [balance, setBalance] = useState(0);
  const [eventCost, setEventCost] = useState(699);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddMoney, setShowAddMoney] = useState(false);
  const [addAmount, setAddAmount] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success' | 'failed' | 'error' | null
  const [paymentAmount, setPaymentAmount] = useState(null);
  const [plans, setPlans] = useState([]);
  const [storageTiers, setStorageTiers] = useState([]);
  const [selectedTierSlug, setSelectedTierSlug] = useState("500gb");
  const [currentPlan, setCurrentPlan] = useState(null);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [storageUsage, setStorageUsage] = useState(null);
  const [recurringStatus, setRecurringStatus] = useState(null);
  const [cancellingRecurring, setCancellingRecurring] = useState(false);
  const formRef = useRef(null);
  const [currency, setCurrency] = useState(() => detectCurrency());
  const [exchangeRates, setExchangeRates] = useState([]);

  // Admin impersonation — admin_token present means an admin is acting as this studio
  const adminToken = localStorage.getItem("admin_token");
  const [adminAddAmount, setAdminAddAmount] = useState("");
  const [adminAddLoading, setAdminAddLoading] = useState(false);
  const [adminPlanLoading, setAdminPlanLoading] = useState(null);
  const [adminConfirm, setAdminConfirm] = useState(null); // { message, onConfirm }
  const [adminSuccess, setAdminSuccess] = useState(null);

  const userId = localStorage.getItem("userId");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/exchange-rates`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setExchangeRates(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const handleCurrencyChange = (c) => {
    setCurrency(c);
    localStorage.setItem("photentra_currency", c);
  };

  // Check URL params for payment status (PayU redirects back here)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("payment");
    const storageUpgraded = params.get("storage_upgraded");
    if (storageUpgraded) {
      setPaymentStatus("success");
      setPaymentAmount(null);
      window.history.replaceState({}, document.title, "/wallet");
    } else if (status) {
      setPaymentStatus(status);
      setPaymentAmount(params.get("amount"));
      window.history.replaceState({}, document.title, "/wallet");
    }
  }, [location]);

  const fetchWallet = useCallback(async () => {
    try {
      const [walletRes, txRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/wallet/${userId}`),
        fetch(`${API_BASE_URL}/api/wallet/${userId}/transactions`),
      ]);
      if (walletRes.ok) {
        const data = await walletRes.json();
        setBalance(data.balance);
        setEventCost(data.event_cost);
      }
      if (txRes.ok) {
        const data = await txRes.json();
        // Assigning the raw body meant a `{ transactions: [...] }` envelope left
        // this as an object, and the render crashed on .map.
        setTransactions(Array.isArray(data) ? data : data.transactions || []);
      }
    } catch (err) {
      console.error("Failed to load wallet", err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchWallet(); }, [fetchWallet]);

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE_URL}/api/subscription-plans`).then((r) => r.json()),
      fetch(`${API_BASE_URL}/api/user/${userId}`).then((r) => r.json()),
      fetch(`${API_BASE_URL}/api/storage-tiers`).then((r) => r.json()).catch(() => []),
    ]).then(([plansData, userData, tiersData]) => {
      const plansArr = Array.isArray(plansData) ? plansData : [];
      setPlans(plansArr);
      if (Array.isArray(tiersData) && tiersData.length > 0) {
        setStorageTiers(tiersData);
        setSelectedTierSlug(tiersData[0].slug);
      }
      const user = userData.user || {};
      setCurrentPlan(user.subscription_type || null);
      if (user.subscription_type === "free_trial" && user.trial_started_at) {
        const trialPlan = plansArr.find((p) => p.slug === "free_trial");
        const durationDays = trialPlan?.trial_duration_days ?? 30;
        const endsAt = new Date(user.trial_started_at).getTime() + durationDays * 86400000;
        setTrialDaysLeft(Math.max(0, Math.ceil((endsAt - Date.now()) / 86400000)));
      }
      if (user.subscription_type === "pro_max_ultra") {
        fetch(`${API_BASE_URL}/api/users/${userId}/storage-usage`)
          .then((r) => r.json())
          .then((d) => setStorageUsage(d))
          .catch(() => {});
      }
      // Fetch recurring status for any paid plan
      if (user.subscription_type && user.subscription_type !== 'free') {
        fetch(`${API_BASE_URL}/api/subscription/recurring-status/${userId}`)
          .then((r) => r.json())
          .then((d) => setRecurringStatus(d))
          .catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  const eventsAffordable = eventCost > 0 ? Math.floor(balance / eventCost) : 0;

  const handleAddMoney = async () => {
    const amount = parseFloat(addAmount);
    if (!amount || amount < 1) return;
    setPaymentLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/wallet/initiate-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: parseInt(userId), amount, currency }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.message || "Failed to initiate payment");
        setPaymentLoading(false);
        return;
      }

      submitPaymentForm(formRef.current, data.payuUrl, data.params);
    } catch (err) {
      alert("Network error. Please try again.");
      setPaymentLoading(false);
    }
  };

  const showAdminSuccess = (msg) => {
    setAdminSuccess(msg);
    setTimeout(() => setAdminSuccess(null), 3500);
  };

  const doAdminAddMoney = async () => {
    const amount = parseFloat(adminAddAmount);
    if (!amount || amount < 1) return;
    setAdminAddLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/wallet/credit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ user_id: parseInt(userId), amount, payment_method: "admin_grant", description: "Admin wallet top-up" }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.message || "Failed to add balance"); return; }
      setAdminAddAmount("");
      fetchWallet();
      showAdminSuccess(`₹${amount.toLocaleString("en-IN")} added to wallet successfully.`);
    } catch { alert("Network error. Please try again."); }
    finally { setAdminAddLoading(false); }
  };

  const handleAdminAddMoney = () => {
    const amount = parseFloat(adminAddAmount);
    if (!amount || amount < 1) return;
    setAdminConfirm({
      message: `Add ₹${amount.toLocaleString("en-IN")} to this studio's wallet?\n\nThis will be credited immediately without any payment.`,
      onConfirm: doAdminAddMoney,
    });
  };

  const doAdminActivatePlan = async (planSlug) => {
    setAdminPlanLoading(planSlug);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/subscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ plan_slug: planSlug, note: "Activated by admin (no payment)" }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.message || "Failed to activate plan"); return; }
      setCurrentPlan(planSlug);
      fetchWallet();
      showAdminSuccess(`Plan activated successfully. The studio is now on the ${planSlug} plan.`);
    } catch { alert("Network error. Please try again."); }
    finally { setAdminPlanLoading(null); }
  };

  const handleAdminActivatePlan = (planSlug, planName) => {
    setAdminConfirm({
      message: `Activate the ${planName} plan for this studio?\n\nThis bypasses payment — the plan will start immediately at no charge.`,
      onConfirm: () => doAdminActivatePlan(planSlug),
    });
  };

  const quickAmounts = [500, 1000, 2000, 5000];

  return (
    <div className="wlt-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="wlt-main-content">
        <header className="wlt-header">
          <div className="wlt-header-left">
            <button className="wlt-toggle-sidebar" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <h1 className="wlt-page-title">Wallet</h1>
          </div>
        </header>

        <main className="wlt-main">
          {/* Payment Status Banner */}
          {paymentStatus && (
            <div className={`wlt-payment-banner wlt-payment-banner--${paymentStatus}`}>
              {paymentStatus === "success" && (
                <>
                  <span className="wlt-payment-banner__icon">✅</span>
                  <span>Payment successful! ₹{paymentAmount ? parseFloat(paymentAmount).toFixed(2) : ""} has been added to your wallet.</span>
                </>
              )}
              {paymentStatus === "failed" && (
                <>
                  <span className="wlt-payment-banner__icon">❌</span>
                  <span>Payment failed or was cancelled. No amount was deducted. Please try again.</span>
                </>
              )}
              {paymentStatus === "error" && (
                <>
                  <span className="wlt-payment-banner__icon">⚠️</span>
                  <span>Something went wrong with payment verification. If money was deducted, it will be refunded within 48 hours. Contact support if needed.</span>
                </>
              )}
              <button className="wlt-payment-banner__close" onClick={() => setPaymentStatus(null)}>✕</button>
            </div>
          )}

          {/* Admin success banner */}
          {adminSuccess && (
            <div className="wlt-payment-banner wlt-payment-banner--success">
              <span className="wlt-payment-banner__icon">✅</span>
              <span>{adminSuccess}</span>
              <button className="wlt-payment-banner__close" onClick={() => setAdminSuccess(null)}>✕</button>
            </div>
          )}

          {/* Subscription warning / grace / locked banners */}
          {storageUsage?.is_locked && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderLeft: "4px solid #ef4444", borderRadius: 8, padding: "14px 16px", marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 12 }}>
              <span style={{ fontSize: 20, lineHeight: 1 }}>🔒</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: "#b91c1c", fontSize: 14, marginBottom: 4 }}>
                  Account locked — subscription expired over 30 days ago
                </div>
                <div style={{ fontSize: 13, color: "#991b1b", lineHeight: 1.5 }}>
                  Your files and events are safe. Renew now to restore full access instantly.
                </div>
                <button
                  onClick={() => navigate("/subscribe/pro_max_ultra")}
                  style={{ marginTop: 10, padding: "7px 16px", background: "#dc2626", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                >
                  Renew Now
                </button>
              </div>
            </div>
          )}
          {!storageUsage?.is_locked && storageUsage?.in_grace_period && (
            <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderLeft: "4px solid #f97316", borderRadius: 8, padding: "14px 16px", marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 12 }}>
              <span style={{ fontSize: 20, lineHeight: 1 }}>⏳</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: "#c2410c", fontSize: 14, marginBottom: 4 }}>
                  Subscription expired — {storageUsage.grace_days_remaining} day{storageUsage.grace_days_remaining !== 1 ? "s" : ""} left before account lock
                </div>
                <div style={{ fontSize: 13, color: "#9a3412", lineHeight: 1.5 }}>
                  Your existing files and events are safe. New uploads and events are paused. After {storageUsage.grace_days_remaining} day{storageUsage.grace_days_remaining !== 1 ? "s" : ""}, your account will be locked until you renew.
                </div>
                <button
                  onClick={() => navigate("/subscribe/pro_max_ultra")}
                  style={{ marginTop: 10, padding: "7px 16px", background: "#ea580c", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                >
                  Renew Now
                </button>
              </div>
            </div>
          )}
          {!storageUsage?.is_locked && !storageUsage?.in_grace_period && storageUsage?.warn_days_remaining > 0 && (
            <div style={{ background: "#fefce8", border: "1px solid #fde68a", borderLeft: "4px solid #eab308", borderRadius: 8, padding: "14px 16px", marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 12 }}>
              <span style={{ fontSize: 20, lineHeight: 1 }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: "#854d0e", fontSize: 14, marginBottom: 4 }}>
                  Subscription expires in {storageUsage.warn_days_remaining} day{storageUsage.warn_days_remaining !== 1 ? "s" : ""}
                </div>
                <div style={{ fontSize: 13, color: "#713f12", lineHeight: 1.5 }}>
                  {storageUsage.billing_cycle === "yearly"
                    ? "Your annual plan is in its final month. Renew to keep your storage and features active."
                    : "Your monthly plan is expiring soon. Renew now to avoid any interruption."}
                </div>
                <button
                  onClick={() => navigate("/subscribe/pro_max_ultra")}
                  style={{ marginTop: 10, padding: "7px 16px", background: "#ca8a04", color: "#fff", border: "none", borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                >
                  Renew Now
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <CameraLoader text="Loading wallet..." />
          ) : (
            <>
              {/* Balance Cards */}
              <div className="wlt-cards">
                <div className="wlt-card wlt-card--balance">
                  <div className="wlt-card__label">Available Balance</div>
                  <div className="wlt-card__value">₹{Number(balance || 0).toFixed(2)}</div>
                </div>
                <div className="wlt-card wlt-card--cost">
                  <div className="wlt-card__label">Per Event Cost</div>
                  <div className="wlt-card__value">₹{Number(eventCost || 0).toFixed(2)}</div>
                </div>
                <div className="wlt-card wlt-card--events">
                  <div className="wlt-card__label">Events You Can Create</div>
                  <div className="wlt-card__value">{eventsAffordable}</div>
                </div>
              </div>

              {/* Add Money Section */}
              <div className="wlt-add-money-section">
                {adminToken ? (
                  /* Admin impersonation mode — direct top-up, no payment gateway */
                  <div className="wlt-add-money-card" style={{ borderColor: "#7c3aed" }}>
                    <div className="wlt-add-money-card__header">
                      <h3 style={{ color: "#7c3aed" }}>Admin: Add Balance</h3>
                    </div>
                    <p style={{ fontSize: 13, color: "#7c3aed", marginBottom: 12 }}>Admin mode — credited instantly, no payment gateway</p>
                    <div className="wlt-add-money-card__quick">
                      {quickAmounts.map((amt) => (
                        <button
                          key={amt}
                          className={`wlt-quick-amt ${adminAddAmount === String(amt) ? "wlt-quick-amt--active" : ""}`}
                          onClick={() => setAdminAddAmount(String(amt))}
                        >
                          ₹{amt.toLocaleString("en-IN")}
                        </button>
                      ))}
                    </div>
                    <div className="wlt-add-money-card__input-row">
                      <span className="wlt-input-prefix">₹</span>
                      <input
                        type="number"
                        min="1"
                        placeholder="Enter amount"
                        value={adminAddAmount}
                        onChange={(e) => setAdminAddAmount(e.target.value)}
                        className="wlt-amount-input"
                      />
                    </div>
                    <button
                      className="wlt-pay-btn"
                      disabled={!adminAddAmount || parseFloat(adminAddAmount) < 1 || adminAddLoading}
                      onClick={handleAdminAddMoney}
                      style={{ background: "#7c3aed" }}
                    >
                      {adminAddLoading ? "Adding..." : `Add ₹${adminAddAmount ? parseFloat(adminAddAmount).toLocaleString("en-IN") : "0"} to Wallet`}
                    </button>
                  </div>
                ) : !showAddMoney ? (
                  <button className="wlt-add-money-btn" onClick={() => setShowAddMoney(true)}>
                    + Add Money to Wallet
                  </button>
                ) : (
                  <div className="wlt-add-money-card">
                    <div className="wlt-add-money-card__header">
                      <h3>Add Money</h3>
                      <button className="wlt-add-money-card__close" onClick={() => { setShowAddMoney(false); setAddAmount(""); }}>✕</button>
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <CurrencySelector value={currency} onChange={handleCurrencyChange} rates={exchangeRates} />
                    </div>
                    <div className="wlt-add-money-card__quick">
                      {quickAmounts.map((amt) => (
                        <button
                          key={amt}
                          className={`wlt-quick-amt ${addAmount === String(amt) ? "wlt-quick-amt--active" : ""}`}
                          onClick={() => setAddAmount(String(amt))}
                        >
                          {formatPrice(amt, currency, exchangeRates)}
                        </button>
                      ))}
                    </div>
                    <div className="wlt-add-money-card__input-row">
                      {/* Always ₹ — the amount typed here is INR (matching the quick-amount
                          buttons and every other price in this flow), never the selected
                          currency. The converted equivalent shows just below instead. */}
                      <span className="wlt-input-prefix">₹</span>
                      <input
                        type="number"
                        min="1"
                        placeholder="Enter amount in INR"
                        value={addAmount}
                        onChange={(e) => setAddAmount(e.target.value)}
                        className="wlt-amount-input"
                      />
                    </div>
                    {currency !== "INR" && addAmount && parseFloat(addAmount) > 0 && (
                      <p className="wlt-pay-note" style={{ marginTop: -6, marginBottom: 8 }}>
                        ≈ {formatPrice(addAmount, currency, exchangeRates)}
                      </p>
                    )}
                    <button
                      className="wlt-pay-btn"
                      disabled={!addAmount || parseFloat(addAmount) < 1 || paymentLoading}
                      onClick={handleAddMoney}
                    >
                      {paymentLoading
                        ? "Redirecting to PayU..."
                        : `Pay ${formatPrice(addAmount || 0, currency, exchangeRates)}`}
                    </button>
                    <p className="wlt-pay-note">You will be redirected to PayU secure payment page</p>
                    {currency !== "INR" && (
                      <p className="wlt-pay-note" style={{ color: "#6366f1", marginTop: 4 }}>
                        Wallet will be credited in INR equivalent
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Info Note */}
              {!adminToken && (
                <div className="wlt-info">
                  <span className="wlt-info__icon">ℹ️</span>
                  <span>Add money instantly using UPI, Credit/Debit Card, or Net Banking. International cards accepted via PayU. For bulk credits, contact the Photentra team.</span>
                </div>
              )}

              {/* Auto-Renewal Status */}
              {recurringStatus && (
                <div style={{ margin: "20px 0 0", padding: "14px 16px", borderRadius: 10, border: "1px solid #d1fae5", background: recurringStatus.recurring_enabled ? "#f0fdf4" : "#f9fafb", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 20 }}>{recurringStatus.recurring_enabled ? "🔄" : "⏸️"}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: recurringStatus.recurring_enabled ? "#166534" : "#374151" }}>
                      {recurringStatus.recurring_enabled ? `Auto-renewal is ON (${recurringStatus.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly'})` : "Auto-renewal is OFF"}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                      {recurringStatus.recurring_enabled
                        ? "Your subscription renews automatically via PayU mandate. You'll never lose access unexpectedly."
                        : "Set up auto-renewal by subscribing via PayU — never miss a renewal."}
                    </div>
                  </div>
                  {recurringStatus.recurring_enabled && (
                    <button
                      disabled={cancellingRecurring}
                      onClick={async () => {
                        if (!window.confirm("Cancel auto-renewal? Your current plan stays active until it expires, then you'll need to renew manually.")) return;
                        setCancellingRecurring(true);
                        try {
                          const r = await fetch(`${API_BASE_URL}/api/subscription/cancel-recurring`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
                            body: JSON.stringify({ user_id: parseInt(userId) }),
                          });
                          const d = await r.json();
                          if (r.ok) {
                            setRecurringStatus((s) => ({ ...s, recurring_enabled: false }));
                            alert(d.message || "Auto-renewal cancelled.");
                          } else {
                            alert(d.message || "Could not cancel. Please contact support.");
                          }
                        } catch {
                          alert("Network error. Please try again.");
                        } finally {
                          setCancellingRecurring(false);
                        }
                      }}
                      style={{ padding: "7px 14px", borderRadius: 7, border: "1px solid #fca5a5", background: "#fff", color: "#dc2626", fontWeight: 600, fontSize: 12, cursor: "pointer", flexShrink: 0 }}
                    >
                      {cancellingRecurring ? "Cancelling..." : "Cancel"}
                    </button>
                  )}
                </div>
              )}

              {/* Subscription Plans */}
              {plans.length > 0 && (
                <div className="wlt-section" style={{ marginTop: 24 }}>
                  <h2 className="wlt-section__title">Subscription Plans</h2>
                  <p className="wlt-pay-note" style={{ marginBottom: 16 }}>
                    Your events and photos stay online as long as your account is active. If an
                    account has no login, no new events, and no active subscription for 12 months,
                    we'll email a warning and give a further 30 days before removing that storage —
                    logging in or creating an event at any point cancels it.
                  </p>
                  {currentPlan === "free_trial" && (
                    <p className="wlt-pay-note" style={{ marginBottom: 16 }}>
                      {trialDaysLeft !== null && trialDaysLeft > 0
                        ? `You're on your free trial — ${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left.`
                        : "Your free trial has ended — pick a plan below to keep creating events, or continue on Free (pay-per-event from wallet)."}
                    </p>
                  )}
                  <div style={{ marginBottom: 12, display: "flex", justifyContent: "flex-end" }}>
                    <CurrencySelector value={currency} onChange={handleCurrencyChange} rates={exchangeRates} />
                  </div>
                  <div className="wlt-plans-grid">
                    {plans.filter((plan) => plan.slug !== "free" && plan.slug !== "free_trial").map((plan) => {
                      const isCurrent = currentPlan === plan.slug;
                      const isUltra = plan.slug === "pro_max_ultra";
                      const activeTier = isUltra && storageTiers.length > 0
                        ? (storageTiers.find((t) => t.slug === selectedTierSlug) || storageTiers[0])
                        : null;
                      return (
                        <div key={plan.slug} className={`wlt-plan-card${isCurrent ? " wlt-plan-card--active" : ""}${isUltra ? " wlt-plan-card--ultra" : ""}`}>
                          {isCurrent && <div className="wlt-plan-card__badge">Current Plan</div>}
                          <div className="wlt-plan-card__name">{plan.name}</div>
                          {isUltra && storageTiers.length > 0 ? (
                            <>
                              <div className="wlt-tier-pills">
                                {storageTiers.map((t) => (
                                  <button
                                    key={t.slug}
                                    className={`wlt-tier-pill${selectedTierSlug === t.slug ? " wlt-tier-pill--active" : ""}`}
                                    onClick={() => setSelectedTierSlug(t.slug)}
                                  >
                                    {t.name}
                                  </button>
                                ))}
                              </div>
                              <div className="wlt-plan-card__price">
                                {formatPrice(activeTier.price_monthly, currency, exchangeRates)}
                                <span style={{ fontSize: 13, fontWeight: 400, opacity: 0.7 }}>/mo</span>
                              </div>
                              <div style={{ fontSize: 12, color: "#d97706", marginTop: -6, marginBottom: 8 }}>
                                or {formatPrice(activeTier.price_yearly, currency, exchangeRates)}/year — 1 month free
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="wlt-plan-card__price">
                                {formatPrice(plan.price, currency, exchangeRates)}
                                <span style={{ fontSize: 13, fontWeight: 400, opacity: 0.7 }}>/mo</span>
                              </div>
                              {plan.price_yearly != null && (
                                <div style={{ fontSize: 12, color: "#16a34a", marginTop: -8, marginBottom: 8 }}>
                                  or {formatPrice(plan.price_yearly, currency, exchangeRates)}/year — 2 months free
                                </div>
                              )}
                            </>
                          )}
                          <ul className="wlt-plan-card__features">
                            {planFeatures(plan, activeTier ? activeTier.name : null).map((f) => <li key={f}>✓ {f}</li>)}
                          </ul>
                          {isCurrent && isUltra && storageUsage && storageUsage.max_storage_bytes && (
                            <div style={{ margin: "12px 0 4px" }}>
                              {storageUsage.storage_tier_name && (
                                <div style={{ fontSize: 11, color: "#d97706", fontWeight: 600, marginBottom: 4 }}>
                                  Current tier: {storageUsage.storage_tier_name}
                                </div>
                              )}
                              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4, color: "#6b7280" }}>
                                <span>Storage used</span>
                                <span>{formatStorageBytes(storageUsage.storage_used_bytes)} / {formatStorageBytes(storageUsage.max_storage_bytes)}</span>
                              </div>
                              <div style={{ height: 6, borderRadius: 3, background: "#e5e7eb", overflow: "hidden" }}>
                                <div style={{ height: "100%", borderRadius: 3, background: storageUsage.percent_used >= 90 ? "#ef4444" : "#f59e0b", width: `${Math.min(100, storageUsage.percent_used || 0).toFixed(1)}%`, transition: "width 0.4s" }} />
                              </div>
                              {storageUsage.percent_used >= 80 && (
                                <div style={{ fontSize: 11, color: storageUsage.percent_used >= 90 ? "#dc2626" : "#d97706", marginTop: 4 }}>
                                  {storageUsage.percent_used >= 90 ? "Almost full — upgrade storage to keep uploading." : "Storage is getting full — consider upgrading."}
                                </div>
                              )}
                            </div>
                          )}
                          <button
                            className={`wlt-pay-btn${isCurrent ? " wlt-pay-btn--disabled" : ""}`}
                            disabled={isCurrent || adminPlanLoading === plan.slug}
                            onClick={() => adminToken ? handleAdminActivatePlan(plan.slug, plan.name) : navigate(`/subscribe/${plan.slug}`)}
                            style={!isCurrent && (adminToken || isUltra) ? { background: isUltra ? "#d97706" : "#7c3aed" } : undefined}
                          >
                            {isCurrent ? "Active" : adminToken
                              ? (adminPlanLoading === plan.slug ? "Activating..." : `Admin: Activate ${plan.name}`)
                              : (isUltra ? "Get Pro Max" : `Choose ${plan.name}`)}
                          </button>
                          {/* Upgrade storage button — shown to active ultra subscribers */}
                          {isCurrent && isUltra && !adminToken && (
                            <button
                              onClick={() => navigate(`/subscribe/${plan.slug}`)}
                              style={{ marginTop: 8, width: "100%", padding: "10px", borderRadius: 8, border: "1.5px solid #f59e0b", background: "#fffbeb", color: "#b45309", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                            >
                              Upgrade Storage (500 GB → 10 TB)
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Transaction History */}
              <div className="wlt-section">
                <h2 className="wlt-section__title">Transaction History</h2>
                {transactions.length === 0 ? (
                  <div className="wlt-empty">No transactions yet</div>
                ) : (
                  <div className="wlt-table-wrap">
                    <table className="wlt-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Description</th>
                          <th>Type</th>
                          <th>Method</th>
                          <th>Ref ID</th>
                          <th className="wlt-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((tx) => (
                          <tr key={tx.id}>
                            <td className="wlt-table__date">
                              {new Date(tx.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                              <span className="wlt-table__time">
                                {new Date(tx.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                              </span>
                            </td>
                            <td>{tx.description || "—"}</td>
                            <td>
                              <span className={`wlt-badge wlt-badge--${tx.type}`}>
                                {tx.type === "credit" ? "Credit" : "Debit"}
                              </span>
                            </td>
                            <td>
                              {tx.payment_method === "admin_grant"
                                ? <span style={{ color: "#7c3aed", fontWeight: 600, fontSize: 12 }}>Admin</span>
                                : tx.payment_method || "—"}
                            </td>
                            <td className="wlt-table__ref">{tx.transaction_ref || "—"}</td>
                            <td className={`wlt-right wlt-amount wlt-amount--${tx.type}`}>
                              {tx.type === "credit" ? "+" : "−"}₹{parseFloat(tx.amount).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>
      {/* Hidden form for PayU redirect */}
      <form ref={formRef} style={{ display: "none" }} />

      {/* Admin confirmation modal */}
      {adminConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setAdminConfirm(null)}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, maxWidth: 380, width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <span style={{ fontSize: 22 }}>🔐</span>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#7c3aed" }}>Admin Action</h3>
            </div>
            <p style={{ margin: "0 0 20px", fontSize: 14, color: "#374151", lineHeight: 1.6, whiteSpace: "pre-line" }}>
              {adminConfirm.message}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setAdminConfirm(null)}
                style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", fontSize: 14 }}>
                Cancel
              </button>
              <button
                onClick={() => { adminConfirm.onConfirm(); setAdminConfirm(null); }}
                style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#7c3aed", color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Wallet;
